import { useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useStats } from '../hooks/useStats'
import { useAuth } from '../hooks/useAuth'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import { Button, Eyebrow, Plant, type Stage } from '../components/ui'
import ContinueOnIphone from '../components/ContinueOnIphone'

const STAGE_LABELS = ['Seed', 'Sprout', 'Sapling', 'Forest'] as const

export default function Today() {
  const { stats, loading, error } = useStats()
  const { session } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const email = session?.user.email ?? ''
  const firstName = email.split('@')[0]

  // Dynamic import keeps the large book-title data out of the main bundle; only a 0-book taste-link arrival needs it.
  const tasteSlug: string | undefined = session?.user.user_metadata?.acquisition?.taste
  const [tasteQuery, setTasteQuery] = useState<string | null | undefined>(tasteSlug ? undefined : null)
  useEffect(() => {
    if (!tasteSlug) return
    let cancelled = false
    import('../content/books').then(({ getBookBySlug }) => {
      if (!cancelled) setTasteQuery(getBookBySlug(tasteSlug)?.title ?? null)
    }).catch(() => { if (!cancelled) setTasteQuery(null) })
    return () => { cancelled = true }
  }, [tasteSlug])

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMessage message={error} />
  // Only the post-auth landing redirects; Today from the nav keeps its empty state.
  const postAuth = (location.state as { postAuth?: boolean } | null)?.postAuth === true
  if (postAuth && stats?.totalBooks === 0) {
    // A known taste slug pre-selects its title; otherwise the plain empty picker.
    if (tasteSlug && tasteQuery === undefined) return null
    return (
      <Navigate to={tasteQuery ? `/library/add?q=${encodeURIComponent(tasteQuery)}` : '/library/add'} replace />
    )
  }

  const now = new Date()
  const dateLabel = now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
  const greeting = now.getHours() < 12 ? 'Good morning' : now.getHours() < 18 ? 'Good afternoon' : 'Good evening'

  const due = stats?.questionsDueToday ?? 0

  // Older backends omit gardenStages; an empty garden hides the card instead of showing fake numbers.
  const gardenStages = stats?.gardenStages
  const garden: [Stage, number][] = gardenStages
    ? [[0, gardenStages.seed], [1, gardenStages.sprout], [2, gardenStages.sapling], [3, gardenStages.forest]]
    : []
  const gardenTotal = Math.max(1, garden.reduce((n, [, count]) => n + count, 0))

  // Forecast index 0 is today; heatmap is 84 days, oldest first.
  const forecast = stats?.forecastDueByDay ?? []
  const forecastMax = Math.max(1, ...forecast)
  const heatmap = stats?.reviewHistoryByDay ?? []
  const heatmapMax = Math.max(1, ...heatmap)
  const hasReviewHistory = heatmap.some(v => v > 0)

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Eyebrow>{dateLabel}</Eyebrow>
      {/* firstName is free text with no break points; overflowWrap keeps it inside the viewport. */}
      <Typography sx={{ fontFamily: 'var(--font-serif)', fontWeight: 500, fontSize: 'clamp(28px,4vw,40px)', letterSpacing: '-0.8px', color: 'var(--ink)', mt: 1, mb: 3, overflowWrap: 'anywhere' }}>
        {greeting}, <em style={{ color: 'var(--moss)' }}>{firstName}.</em>
      </Typography>

      {stats && stats.totalBooks === 0 ? (
        <Box sx={{ ...cardSx }}>
          <Typography sx={{ fontFamily: 'var(--font-serif)', fontSize: 24, mb: 1 }}>Plant your first book.</Typography>
          <Typography sx={{ color: 'var(--ink-2)', mb: 2 }}>Import a book and Recall turns its highlights into a daily tutor.</Typography>
          <Button to="/library/add" variant="primary">Add a book</Button>
          <ContinueOnIphone placement="today_empty" />
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: garden.length ? { xs: '1fr', md: '1.5fr 1fr' } : '1fr', gap: 2 }}>
          <Box
            sx={{
              borderRadius: 'var(--r-xl)',
              p: 3.5,
              position: 'relative',
              overflow: 'hidden',
              background: 'linear-gradient(150deg, var(--moss), var(--moss-dim))',
              color: 'var(--moss-ink)',
            }}
          >
            <Box sx={{ position: 'absolute', right: -10, bottom: -20, opacity: 0.18 }}>
              <Plant stage={3} size={150} color="var(--moss-ink)" soil={false} />
            </Box>
            <Box sx={{ position: 'relative' }}>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: 1.8, textTransform: 'uppercase', opacity: 0.85 }}>
                {due > 0 ? 'Session ready' : 'Caught up'}
              </span>
              <Typography sx={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(26px,3vw,36px)', lineHeight: 1.1, letterSpacing: '-0.6px', mt: 1.5, maxWidth: '16ch' }}>
                {due > 0 ? (
                  <>
                    {due} concept{due === 1 ? '' : 's'} <em>ready to be watered.</em>
                  </>
                ) : (
                  <>You're all caught up.</>
                )}
              </Typography>
              <Typography sx={{ opacity: 0.85, mt: 1.5, fontSize: 14 }}>
                {due > 0
                  ? 'A short conversation — about six minutes.'
                  : stats && stats.nextReviewInHours > 0
                    ? `Next review in ${stats.nextReviewInHours} hour${stats.nextReviewInHours === 1 ? '' : 's'}.`
                    : 'Nothing due right now.'}
              </Typography>
              {due > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Button onClick={() => navigate('/quiz')} variant="cream">
                    Start session
                  </Button>
                </Box>
              )}
            </Box>
          </Box>

          {garden.length > 0 && (
          <Box sx={{ ...cardSx }}>
            <Eyebrow>Your garden</Eyebrow>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              {garden.map(([stage, count]) => (
                <Box key={stage} sx={{ textAlign: 'center' }}>
                  <Plant stage={stage} size={32} soil={false} />
                  <Typography sx={{ fontWeight: 600, fontSize: 16, mt: 0.5 }}>{count}</Typography>
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: 'var(--ink-3)' }}>
                    {STAGE_LABELS[stage]}
                  </span>
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', height: 6, borderRadius: 999, overflow: 'hidden', mt: 2.5 }}>
              {garden.map(([stage, count]) =>
                count > 0 ? (
                  <Box key={stage} sx={{ flex: count / gardenTotal, background: stage === 0 ? 'var(--loam)' : 'var(--moss)' }} />
                ) : null,
              )}
            </Box>
          </Box>
          )}
        </Box>
      )}

      {stats && stats.totalBooks > 0 && forecast.length > 0 && (
        <Box sx={{ ...cardSx, mt: 2 }}>
          <Eyebrow>Next 14 days</Eyebrow>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75, height: 72, mt: 2 }}>
            {forecast.map((count, i) => (
              <Box
                key={i}
                title={`${count} due`}
                sx={{
                  flex: 1,
                  height: `${6 + (count / forecastMax) * 58}px`,
                  borderRadius: 1,
                  background: i === 0 ? 'var(--moss)' : count > 0 ? 'var(--moss-line)' : 'var(--border)',
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {stats && stats.totalBooks > 0 && hasReviewHistory && (
        <Box sx={{ ...cardSx, mt: 2 }}>
          <Eyebrow>Last 12 weeks</Eyebrow>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 0.75, mt: 2, maxWidth: 360 }}>
            {Array.from({ length: 12 }).map((_, col) => (
              <Box key={col} sx={{ display: 'grid', gridTemplateRows: 'repeat(7, 1fr)', gap: 0.75 }}>
                {Array.from({ length: 7 }).map((_, row) => {
                  const count = heatmap[col * 7 + row] ?? 0
                  return (
                    <Box
                      key={row}
                      title={count > 0 ? `${count} review${count === 1 ? '' : 's'}` : 'No reviews'}
                      sx={{
                        aspectRatio: '1',
                        borderRadius: '3px',
                        background: count > 0 ? 'var(--moss)' : 'var(--border)',
                        opacity: count > 0 ? 0.25 + (count / heatmapMax) * 0.75 : 1,
                      }}
                    />
                  )
                })}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  )
}

const cardSx = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--r-lg)',
  p: 3,
} as const
